import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  JournalEntryCard,
  JournalEntryDocChip,
  JournalEntryExternalLink,
  JournalEntryPostedBadge,
} from '@cottage/design-system/internal-tooling/ui'
import JournalEntryPreview from '@/components/JournalEntryPreview'
import WarningBanner from '@/components/WarningBanner'
import { usePageAccess } from '@/hooks/usePageAccess'
import { useQBOData } from '@/hooks/useQBOData'
import {
  generatePacYearEndClose,
  getPacYearEndCloseStatus,
  pushPacYearEndCloseQboUpdate,
  deletePacYearEndCloseFromQbo,
} from '@/services/waterfall'
import {
  getQboIdMappings,
  postJournalEntry,
  updateJournalEntryLines,
  type JournalEntryLine,
  type QboIdMappingItem,
} from '@/services/accounting'
import type { WaterfallJournalEntryDraftItem, WaterfallJeStatusValue } from '@/types/waterfall'
import { formatApiError } from '@/utils/apiError'
import { toast } from '@/store/toastStore'
import { parseJeGenerateError } from './allocationMonthReadinessUtils'
import {
  formatJePostedDate,
} from './waterfallJeStatus'

type Props = {
  month: string
  onPosted?: () => void
}

function isLinesBalanced(lines: JournalEntryLine[]): boolean {
  const debits = lines
    .filter((line) => line.posting_type === 'Debit')
    .reduce((sum, line) => sum + Number(line.amount), 0)
  const credits = lines
    .filter((line) => line.posting_type === 'Credit')
    .reduce((sum, line) => sum + Number(line.amount), 0)
  return Math.abs(debits - credits) < 0.005
}

function defaultGaDepartment(
  mappings: QboIdMappingItem[],
  locations: { id: string; name: string }[],
): Pick<JournalEntryLine, 'department_id' | 'department_name'> {
  const mapping = mappings.find((item) => item.key === 'department.g_and_a')
  if (mapping) {
    return { department_id: mapping.qbo_id, department_name: mapping.display_name }
  }
  const location = locations.find((item) => item.name === 'G&A')
  return location
    ? { department_id: location.id, department_name: location.name }
    : { department_id: null, department_name: null }
}

function deriveJeStatus(
  draft: WaterfallJournalEntryDraftItem | null,
  needsQboUpdate: boolean,
): WaterfallJeStatusValue {
  if (!draft) return 'none'
  if (needsQboUpdate) return 'stale'
  if (draft.status === 'posted') return 'posted'
  return 'draft'
}

export default function PacYearEndClosePanel({ month, onPosted }: Props) {
  const { canEdit } = usePageAccess('distributions')
  const { locations } = useQBOData()
  const [loading, setLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [posting, setPosting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [draft, setDraft] = useState<WaterfallJournalEntryDraftItem | null>(null)
  const [needsQboUpdate, setNeedsQboUpdate] = useState(false)
  const [draftLines, setDraftLines] = useState<JournalEntryLine[]>([])
  const [missingAccountKeys, setMissingAccountKeys] = useState<string[]>([])
  const [accountOptions, setAccountOptions] = useState<{ id: string; label: string }[]>([])
  const [qboMappings, setQboMappings] = useState<QboIdMappingItem[]>([])
  const [postedMenuOpen, setPostedMenuOpen] = useState(false)
  const linesSaveGenerationRef = useRef(0)

  const locationOptions = useMemo(
    () => locations.map((location) => ({ id: location.id, label: location.name })),
    [locations],
  )
  const newLineDefaults = useMemo(
    () => defaultGaDepartment(qboMappings, locations),
    [qboMappings, locations],
  )

  const jeStatus = deriveJeStatus(draft, needsQboUpdate)
  const hasUnpostedDraft = draft?.status === 'draft'
  const isDraft = hasUnpostedDraft && jeStatus !== 'stale'
  const isPosted = draft?.status === 'posted'
  const showDirty = needsQboUpdate || jeStatus === 'stale'
  const balanced = useMemo(() => isLinesBalanced(draftLines), [draftLines])
  const missingLocations = useMemo(
    () => draftLines.some((line) => !line.department_id && !line.department_name?.trim()),
    [draftLines],
  )

  const loadStatus = useCallback(async () => {
    setLoading(true)
    setError(null)
    setMissingAccountKeys([])
    try {
      const data = await getPacYearEndCloseStatus(month)
      setDraft(data.draft)
      setDraftLines((data.draft?.lines as JournalEntryLine[]) ?? [])
      setNeedsQboUpdate(false)
    } catch (e) {
      setDraft(null)
      setDraftLines([])
      setError(formatApiError(e, 'Failed to load Partner Allocation Clearing close'))
    } finally {
      setLoading(false)
    }
  }, [month])

  useEffect(() => {
    void loadStatus()
  }, [loadStatus])

  useEffect(() => {
    async function loadQboMappings() {
      try {
        const response = await getQboIdMappings()
        setQboMappings(response.mappings)
        setAccountOptions(
          response.mappings
            .filter((mapping) => mapping.mapping_type === 'account')
            .map((mapping) => ({ id: mapping.qbo_id, label: mapping.display_name }))
            .sort((a, b) => a.label.localeCompare(b.label)),
        )
      } catch (err) {
        console.error('Failed to load QBO account mappings:', err)
      }
    }
    void loadQboMappings()
  }, [])

  const handleLinesChange = useCallback(
    async (next: JournalEntryLine[]) => {
      setDraftLines(next)
      if (!draft?.id) return
      const saveGeneration = ++linesSaveGenerationRef.current
      try {
        const updated = await updateJournalEntryLines(draft.id, next)
        if (saveGeneration !== linesSaveGenerationRef.current) return
        setDraftLines(updated.lines)
        setDraft((prev) =>
          prev
            ? {
                ...prev,
                lines: updated.lines as WaterfallJournalEntryDraftItem['lines'],
                total_debits: updated.total_debits,
                total_credits: updated.total_credits,
              }
            : prev,
        )
      } catch (e) {
        if (saveGeneration !== linesSaveGenerationRef.current) return
        toast.error(formatApiError(e, 'Failed to save line changes'))
      }
    },
    [draft?.id],
  )

  const applyGenerateResult = useCallback(
    (result: { draft: WaterfallJournalEntryDraftItem; needs_qbo_update: boolean }) => {
      setDraft(result.draft)
      setDraftLines(result.draft.lines as JournalEntryLine[])
      setNeedsQboUpdate(result.needs_qbo_update)
    },
    [],
  )

  const handleGenerate = async (overwritePosted = false) => {
    if (!canEdit) return
    if (overwritePosted) {
      const confirmed = window.confirm(
        draft?.doc_number
          ? `Overwrite ${draft.doc_number} from the current prior-year net income?`
          : 'Overwrite this journal entry from the current prior-year net income?',
      )
      if (!confirmed) return
    }

    setActionLoading(true)
    setError(null)
    setMissingAccountKeys([])
    try {
      const result = await generatePacYearEndClose({
        month,
        overwrite_posted: overwritePosted,
      })
      applyGenerateResult(result)
      toast.success(
        overwritePosted ? 'Journal entry draft overwritten' : 'Journal entry draft created',
      )
    } catch (e) {
      const parsed = parseJeGenerateError(e)
      setError(parsed.message)
      setMissingAccountKeys(parsed.missingKeys)
      toast.error(parsed.message)
    } finally {
      setActionLoading(false)
    }
  }

  const handleRegenerate = async () => {
    if (!canEdit || !draft) return
    if (isPosted) {
      const confirmed = window.confirm(
        draft.doc_number
          ? `Overwrite ${draft.doc_number} from the current prior-year net income?`
          : 'Overwrite this journal entry from the current prior-year net income?',
      )
      if (!confirmed) return
      await handleGenerate(true)
      return
    }
    await handleGenerate(false)
  }

  const handleDeleteQbo = async () => {
    if (!canEdit || !draft) return
    const baseConfirm = draft.doc_number
      ? `Delete ${draft.doc_number} from QuickBooks?`
      : 'Delete this journal entry from QuickBooks?'
    const confirmed = window.confirm(
      `${baseConfirm} This will also uncheck the prior-year Partner Allocation Clearing close readiness item.`,
    )
    if (!confirmed) return

    setActionLoading(true)
    setPostedMenuOpen(false)
    try {
      const result = await deletePacYearEndCloseFromQbo(draft.id)
      setDraft(result.draft)
      setDraftLines(result.draft.lines as JournalEntryLine[])
      setNeedsQboUpdate(false)
      toast.success('Journal entry deleted from QuickBooks')
      await loadStatus()
      onPosted?.()
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to delete journal entry from QuickBooks'))
    } finally {
      setActionLoading(false)
    }
  }

  const handlePushQboUpdate = async () => {
    if (!canEdit || !draft) return
    const confirmed = window.confirm(
      draft.doc_number
        ? `Update ${draft.doc_number} in QuickBooks from the current prior-year net income?`
        : 'Update this journal entry in QuickBooks from the current prior-year net income?',
    )
    if (!confirmed) return

    setActionLoading(true)
    setPostedMenuOpen(false)
    try {
      const result = await pushPacYearEndCloseQboUpdate(draft.id, {
        force_regenerate: true,
      })
      setDraft(result.draft)
      setDraftLines(result.draft.lines as JournalEntryLine[])
      setNeedsQboUpdate(false)
      toast.success(
        result.regenerated
          ? 'Regenerated draft and updated QuickBooks'
          : 'Updated journal entry in QuickBooks',
      )
      await loadStatus()
      onPosted?.()
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to update QuickBooks'))
    } finally {
      setActionLoading(false)
    }
  }

  const handlePost = async () => {
    if (!canEdit || !draft || !balanced || missingLocations || showDirty) return

    setPosting(true)
    try {
      await updateJournalEntryLines(draft.id, draftLines)
      await postJournalEntry(draft.id)
      toast.success('Journal entry posted to QuickBooks')
      await loadStatus()
      onPosted?.()
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to post journal entry'))
    } finally {
      setPosting(false)
    }
  }

  const canEditDraftLines = canEdit && isDraft && !posting
  const txnDateLabel = formatJePostedDate(draft?.txn_date ?? null)
  const showCreateButton = canEdit && !draft && !loading
  const showRegenerate = canEdit && Boolean(draft) && !isPosted
  const showPost = canEdit && hasUnpostedDraft

  const hasAlerts =
    error ||
    missingAccountKeys.length > 0 ||
    (draft?.warnings ?? []).length > 0 ||
    draft?.error_message ||
    (draft && isDraft && !balanced) ||
    (draft && isDraft && missingLocations) ||
    (draft && showDirty)

  return (
    <div data-readonly-allow>
      <JournalEntryCard
        posted={isPosted}
        title="Close prior-year Partner Allocation Clearing"
        identity={
          <>
            {draft?.doc_number ? (
              <JournalEntryDocChip>{draft.doc_number}</JournalEntryDocChip>
            ) : null}
            {txnDateLabel ? <span>{txnDateLabel}</span> : null}
            {isPosted && draft?.qbo_url ? (
              <JournalEntryExternalLink href={draft.qbo_url}>
                View in QuickBooks
              </JournalEntryExternalLink>
            ) : null}
          </>
        }
        actions={
          <>
            {showCreateButton ? (
              <button
                type="button"
                onClick={() => void handleGenerate(false)}
                disabled={actionLoading}
                className="btn btn-primary text-xs"
              >
                {actionLoading ? 'Creating…' : 'Create JE draft'}
              </button>
            ) : null}

            {showRegenerate ? (
              <button
                type="button"
                onClick={() => void handleRegenerate()}
                disabled={actionLoading || posting}
                className="btn btn-primary text-xs"
              >
                {actionLoading ? 'Regenerating…' : 'Regenerate'}
              </button>
            ) : null}

            {showPost ? (
              <button
                type="button"
                onClick={() => void handlePost()}
                disabled={posting || actionLoading || !balanced || missingLocations || showDirty}
                className="btn btn-primary text-xs"
              >
                {posting ? 'Posting…' : 'Post to QuickBooks'}
              </button>
            ) : null}

            {isPosted ? (
              <JournalEntryPostedBadge
                loading={actionLoading}
                menuOpen={postedMenuOpen}
                onMenuOpenChange={setPostedMenuOpen}
                menu={
                  <>
                    {canEdit ? (
                      <button
                        type="button"
                        role="menuitem"
                        className="block w-full px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-50"
                        disabled={actionLoading}
                        onClick={() => {
                          setPostedMenuOpen(false)
                          void handleRegenerate()
                        }}
                      >
                        Regenerate
                      </button>
                    ) : null}
                    <button
                      type="button"
                      role="menuitem"
                      className="block w-full px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-50"
                      disabled={actionLoading}
                      onClick={() => void handlePushQboUpdate()}
                    >
                      Update Existing JE
                    </button>
                    {canEdit ? (
                      <button
                        type="button"
                        role="menuitem"
                        className="block w-full px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-50"
                        disabled={actionLoading}
                        onClick={() => void handleDeleteQbo()}
                      >
                        Delete Existing JE
                      </button>
                    ) : null}
                  </>
                }
              />
            ) : null}
          </>
        }
        alerts={
          hasAlerts
            ? (
                <>
                  {error ? (
                    <p className="text-xs text-red-600" role="alert">
                      {error}
                    </p>
                  ) : null}
                  {missingAccountKeys.length > 0 ? (
                    <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950">
                      <p className="font-medium">QuickBooks account IDs not configured:</p>
                      <ul className="mt-1 list-disc pl-4">
                        {missingAccountKeys.map((key) => (
                          <li key={key}>
                            <code>{key}</code>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                  {(draft?.warnings ?? []).map((warning, index) => (
                    <WarningBanner key={index} message={warning} />
                  ))}
                  {draft?.error_message ? <WarningBanner message={draft.error_message} /> : null}
                  {draft && isDraft && !balanced ? (
                    <WarningBanner message="Journal entry is out of balance. Adjust lines before posting." />
                  ) : null}
                  {draft && isDraft && missingLocations ? (
                    <WarningBanner message="Assign a location on every line before posting." />
                  ) : null}
                  {draft && showDirty ? (
                    <WarningBanner
                      message="Prior-year net income changed since this draft was saved. Regenerate before posting, or overwrite and update QuickBooks if already posted."
                    />
                  ) : null}
                </>
              )
            : undefined
        }
      >
        <p className="text-xs text-gray-500">
          Reverse prior-year net income (sum of monthly P&L) into Retained Earnings.
          Journal date is January 1 of the selected month.
        </p>
        {draft && draftLines.length > 0 ? (
          <JournalEntryPreview
            lines={draftLines}
            variant="split"
            hideHeader
            scrollable
            className="mt-3 flex max-h-[24rem] min-h-0 flex-col"
            totalDebits={draft.total_debits}
            totalCredits={draft.total_credits}
            accountOptions={accountOptions}
            editableAmounts={canEditDraftLines}
            editableLines={canEditDraftLines}
            editableLocations={canEditDraftLines}
            locationOptions={locationOptions}
            newLineDefaults={newLineDefaults}
            onLinesChange={
              canEditDraftLines ? (next) => void handleLinesChange(next) : undefined
            }
          />
        ) : null}
      </JournalEntryCard>
    </div>
  )
}
